import React, { Component } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';

import createStore from 'lib/store/create';
import { isServer } from 'lib/common/constants';

const STORE = '__NEXT_REDUX_STORE__';

function getOrCreateStore(initialState, thunkContext) {
  // Always make a new store if server, otherwise state is shared between requests
  if (isServer) {
    return createStore(initialState, thunkContext);
  }

  // Create store if unavailable on the client and set it on the window object
  if (!window[STORE]) {
    window[STORE] = createStore(initialState, thunkContext);
  }
  return window[STORE];
}

/**
 * Provides the wrapped `App` with the redux `store`.
 */
const withReduxStore = (App) =>
  class AppWithRedux extends Component {
    static propTypes = {
      /* eslint-disable-next-line react/forbid-prop-types */
      initialState: PropTypes.object.isRequired,
      /* eslint-disable-next-line react/forbid-prop-types */
      i18n: PropTypes.object.isRequired,
    };

    /**
     * Called on the app initial entry-point
     * and each time the route changes.
     *
     * The important thing to remember here is that `getInitialProps`
     * is called for every *server* rendered page, but it's only
     * called on the client *after* a client-side route change.
     *
     * SERVER: `getInitialProps()` -> `init()` -> `render()`
     * CLIENT: `init()` -> `render()` -> route-change -> `getInitialProps()` -> render() -> …
     */
    static async getInitialProps(appContext) {
      const { ctx } = appContext;
      const { i18n } = ctx;

      const initialState = isServer && App.getInitialReduxState(ctx);
      const store = getOrCreateStore(initialState, { i18n });
      ctx.store = store;

      const appProps =
        App.getInitialProps && (await App.getInitialProps(appContext));

      return { ...appProps, initialState: store.getState() };
    }

    constructor(props) {
      super(props);
      const { initialState, i18n } = props;
      this.store = getOrCreateStore(initialState, { i18n });
    }

    render() {
      const appProps = omit(this.props, 'initialState');
      return <App {...appProps} store={this.store} data-testid="app" />;
    }
  };

export default withReduxStore;
